import { useState, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, X } from 'lucide-react';
import { generateId } from '@/shared/lib/id';
import { Opening } from '@/shared/model';
import { roundTo } from '@/shared/lib/number';
import { useToastStore } from '@/stores';

interface OpeningsManagerProps {
  openings: Opening[];
  onChange: (openings: Opening[]) => void;
  maxWallWidth?: number;
  maxWallHeight?: number;
}

export const OpeningsManager: FC<OpeningsManagerProps> = ({
  openings,
  onChange,
  maxWallWidth,
  maxWallHeight,
}) => {
  const { t } = useTranslation();
  const addToast = useToastStore((s) => s.addToast);
  
  const [isAddingOpening, setIsAddingOpening] = useState(false);
  const [newOpening, setNewOpening] = useState({ 
    type: 'window' as 'window' | 'door', 
    width: '' as string | number, 
    height: '' as string | number, 
    hasSlopes: true 
  });

  const handleSaveOpening = () => {
      const wStr = String(newOpening.width !== undefined && newOpening.width !== null ? newOpening.width : '').trim();
      const hStr = String(newOpening.height !== undefined && newOpening.height !== null ? newOpening.height : '').trim();
      if (!wStr || !hStr || !onChange) return;
      
      const widthM = parseFloat(wStr) / 100;
      const heightM = parseFloat(hStr) / 100;
      if (isNaN(widthM) || widthM <= 0 || isNaN(heightM) || heightM <= 0) return;

      if (maxWallWidth !== undefined && widthM > maxWallWidth) {
        addToast(t('geometry.suspicious_opening_width', 'Ширина проёма превышает ширину стены'), 'warning');
      }
      if (maxWallHeight !== undefined && heightM > maxWallHeight) {
        addToast(t('geometry.suspicious_opening_height', 'Высота проёма превышает высоту стены'), 'warning');
      }

      const openingData: Omit<Opening, 'id'> = {
          type: newOpening.type,
          width: widthM, 
          height: heightM,
          hasSlopes: newOpening.hasSlopes
      };

      onChange([...openings, { ...openingData, id: generateId() }]);
      setNewOpening({ ...newOpening, width: '', height: '' });
      setIsAddingOpening(false);
  };

  const handleAddPresetDoor = (leafWidthCm: number) => {
      if (!onChange) return;
      const openingWidthM = (leafWidthCm + 10) / 100;
      const presetHeightM = 2.06;

      // GEOM-03: same wall-dimension guard as handleSaveOpening
      if (maxWallWidth !== undefined && openingWidthM > maxWallWidth) {
        addToast(t('geometry.suspicious_opening_width', 'Ширина проёма превышает ширину стены'), 'warning');
      }
      if (maxWallHeight !== undefined && presetHeightM > maxWallHeight) {
        addToast(t('geometry.suspicious_opening_height', 'Высота проёма превышает высоту стены'), 'warning');
      }

      onChange([...openings, {
          id: generateId(),
          type: 'door',
          width: openingWidthM,
          height: presetHeightM,
          hasSlopes: newOpening.hasSlopes
      }]);
      setIsAddingOpening(false);
  };

  const grouped = (openings || []).reduce((acc: Record<string, Opening & { count: number; ids: (string | undefined)[] }>, op) => {
      const key = `${op.type}-${op.width}-${op.height}-${op.hasSlopes}`;
      if (!acc[key]) acc[key] = { ...op, count: 0, ids: [] as (string | undefined)[] };
      acc[key].count += 1;
      acc[key].ids.push(op.id);
      return acc;
  }, {});

  return (
    <div className="mb-6 border-t border-slate-100 dark:border-slate-800 pt-4">
        <div className="flex justify-between items-center mb-3">
            <div className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t('room_input.openings_title')}</div>
            {!isAddingOpening && (
                <motion.button 
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setIsAddingOpening(true)}
                    className="text-xs font-bold text-blue-600 dark:text-blue-400 flex items-center gap-1 hover:bg-blue-50 dark:hover:bg-blue-900/20 px-2 py-1 rounded transition-colors"
                >
                    <Plus size={14}/> {t('common.add')}
                </motion.button>
            )}
        </div>

        <AnimatePresence>
            {isAddingOpening && (
                <motion.div 
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="bg-slate-50 dark:bg-slate-800/50 p-3 rounded-lg border border-slate-200 dark:border-slate-700 mb-3 overflow-hidden"
                >
                    <div className="flex justify-between items-center mb-2">
                        <span className="text-xs font-bold text-slate-500 dark:text-slate-400">{t('room_input.new_opening')}</span>
                        <button 
                            onClick={() => setIsAddingOpening(false)} 
                            className="p-1 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 cursor-pointer"
                            aria-label={t('common.close')}
                        >
                            <X size={16} aria-hidden="true"/>
                        </button>
                    </div>
                    <div className="flex gap-2 mb-3">
                        <div className="flex w-full md:w-1/3 bg-slate-100 dark:bg-slate-700 p-1 rounded-lg border border-slate-200 dark:border-slate-600">
                            <button
                                type="button"
                                onClick={() => setNewOpening({...newOpening, type: 'window'})}
                                className={`flex-1 flex items-center justify-center gap-2 py-1.5 rounded-md text-[10px] font-bold transition-all ${newOpening.type === 'window' ? 'bg-white dark:bg-slate-600 text-blue-600 dark:text-blue-300 shadow-sm' : 'text-slate-400 dark:text-slate-500'}`}
                            >
                                <span>🪟</span> {t('room_input.window')}
                            </button>
                            <button
                                type="button"
                                onClick={() => setNewOpening({...newOpening, type: 'door'})}
                                className={`flex-1 flex items-center justify-center gap-2 py-1.5 rounded-md text-[10px] font-bold transition-all ${newOpening.type === 'door' ? 'bg-white dark:bg-slate-600 text-amber-600 dark:text-amber-300 shadow-sm' : 'text-slate-400 dark:text-slate-500'}`}
                            >
                                <span>🚪</span> {t('room_input.door')}
                            </button>
                        </div>
                        <input 
                            type="number" placeholder={t('room_input.width_cm_placeholder')}
                            className="w-1/3 p-2 rounded border border-slate-300 dark:border-slate-600 dark:bg-slate-800 text-sm outline-none focus:border-blue-500 dark:text-slate-100"
                            value={newOpening.width}
                            onChange={e => setNewOpening({...newOpening, width: e.target.value})}
                        />
                        <input 
                            type="number" placeholder={t('room_input.height_cm_short_placeholder')}
                            className="w-1/3 p-2 rounded border border-slate-300 dark:border-slate-600 dark:bg-slate-800 text-sm outline-none focus:border-blue-500 dark:text-slate-100"
                            value={newOpening.height}
                            onChange={e => setNewOpening({...newOpening, height: e.target.value})}
                        />
                    </div>

                    <label className="flex items-center gap-2 mb-3 cursor-pointer select-none">
                        <input 
                            type="checkbox"
                            checked={newOpening.hasSlopes}
                            onChange={e => setNewOpening({...newOpening, hasSlopes: e.target.checked})}
                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                        <span className="text-xs text-slate-600 dark:text-slate-400 font-bold">{t('room_input.calc_slopes')}</span>
                    </label>

                    {newOpening.type === 'door' && (
                        <div className="flex items-center gap-2 mb-4">
                            <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase">{t('room_input.leaf')}</span>
                            <div className="flex gap-1">
                                {[60, 70, 80, 90].map(w => (
                                    <button 
                                        key={w}
                                        type="button"
                                        onClick={() => handleAddPresetDoor(w)}
                                        className="px-2.5 py-1 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-md text-[11px] font-bold text-slate-600 dark:text-slate-300 hover:border-amber-400 dark:hover:border-amber-500 hover:text-amber-600 dark:hover:text-amber-400 transition-all shadow-sm active:scale-90"
                                        aria-label={t('room_input.leaf_preset_w', 'Leaf {{w}} cm', { w })}
                                    >
                                        <span className="font-mono tabular-nums">{w}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    <button 
                        onClick={handleSaveOpening}
                        disabled={!String(newOpening.width || '').trim() || !String(newOpening.height || '').trim()}
                        className="w-full py-2 bg-blue-600 text-white rounded font-bold text-sm transition-all"
                    >
                        {t('room_input.add_opening_btn')}
                    </button>
                </motion.div>
            )}
        </AnimatePresence>

        <div className="space-y-2">
            {Object.values(grouped).map((group) => (
                <motion.div 
                    key={group.ids[0]} 
                    layout
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex justify-between items-center p-2 bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded text-sm shadow-sm group"
                >
                    <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded flex items-center justify-center relative ${group.type === 'window' ? 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400' : 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400'}`}>
                            {group.type === 'window' ? '🪟' : '🚪'}
                            <div className="absolute -top-1 -right-1 w-4 h-4 bg-slate-700 dark:bg-slate-600 text-white rounded-full text-[9px] flex items-center justify-center font-bold font-mono tabular-nums">
                                {group.count}
                            </div>
                        </div>
                        <div>
                            <div className="font-bold text-slate-700 dark:text-slate-200 text-xs uppercase flex items-center gap-2">
                                {group.type === 'window' ? t('room_input.window_noun') : t('room_input.door_noun')}
                                {group.hasSlopes && <span className="text-[9px] bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 px-1 rounded">{t('room_input.slopes_tag')}</span>}
                            </div>
                            {/* eslint-disable i18next/no-literal-string */}
                            <div className="text-slate-500 dark:text-slate-400 text-[10px]">
                                <span className="font-mono tabular-nums">{Math.round(Number(group.width) * 100)}</span> x <span className="font-mono tabular-nums">{Math.round(Number(group.height) * 100)}</span> {t('common.cm')}
                                <span className="text-slate-400 dark:text-slate-500 ml-2">-<span className="font-mono tabular-nums">{roundTo(Math.abs((Number(group.width) * Number(group.height) * group.count)), 2)}</span> {t('common.unit_m2')}</span>
                            </div>
                            {/* eslint-enable i18next/no-literal-string */}
                        </div>
                    </div>
                    
                    <div className="flex items-center gap-1">
                        <div className="flex items-center bg-slate-100 dark:bg-slate-700 rounded-lg p-0.5 mr-2">
                            <button 
                                onClick={() => {
                                    const idToRemove = group.ids[group.ids.length - 1];
                                    if (idToRemove) onChange(openings.filter(o => o.id !== idToRemove));
                                }}
                                className="w-7 h-7 flex items-center justify-center rounded-md bg-white dark:bg-slate-600 shadow-sm text-slate-600 dark:text-slate-300 hover:text-red-500 dark:hover:text-red-400 font-bold cursor-pointer"
                                aria-label={t('common.decrease')}
                            >
                                -
                            </button>
                            <div className="w-6 text-center text-xs font-bold font-mono tabular-nums text-slate-600 dark:text-slate-300">{group.count}</div>
                            <button 
                                onClick={() => onChange([...openings, { type: group.type, width: group.width, height: group.height, hasSlopes: group.hasSlopes, id: generateId() }])}
                                className="w-7 h-7 flex items-center justify-center rounded-md bg-white dark:bg-slate-600 shadow-sm text-slate-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 font-bold cursor-pointer"
                                aria-label={t('common.increase')}
                            >
                                +
                            </button>
                        </div>
                        <motion.button 
                            whileHover={{ scale: 1.1 }} 
                            whileTap={{ scale: 0.9 }} 
                            onClick={() => onChange(openings.filter(o => o.id && !group.ids.includes(o.id)))} 
                            className="p-2 text-slate-400 hover:text-red-500 transition-colors cursor-pointer"
                            aria-label={t('common.delete')}
                        >
                            <Trash2 size={16} aria-hidden="true"/>
                        </motion.button>
                    </div>
                </motion.div>
            ))}
        </div>
    </div>
  );
};